import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HOMEKEEP_CONTACT } from 'core/constants';
import { ContactInfo } from 'core/types';

interface ContactsState {
  loading: boolean;
  currentContactLoading: boolean;
  error: string | null;
  currentContact: ContactInfo | null;
  currentContactId: number | null;
  contacts: ContactInfo[];
  newContactModalVisible: boolean;
  editContactModalVisible: boolean;
  contactsListDirty: boolean;
}

export const initialState: ContactsState = {
  loading: false,
  currentContactLoading: false,
  error: null,
  currentContact: null,
  currentContactId: null,
  contacts: [],
  newContactModalVisible: false,
  editContactModalVisible: false,
  contactsListDirty: false
};

export const contactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    fetchContactListStart: (state) => {
      state.loading = true;
    },
    fetchContactListSuccess: (state, action: PayloadAction<any>) => {
      const { items } = action.payload;
      state.contacts = items;
      state.contacts.push(HOMEKEEP_CONTACT);
      state.loading = false;
    },
    fetchContactListError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    clearContactsError: (state) => {
      state.error = null;
    },
    fetchContactStart: (state) => {
      state.currentContactLoading = true;
      state.currentContact = null;
    },
    fetchContactSuccess: (state, action: PayloadAction<any>) => {
      state.currentContact = action.payload;
      state.currentContactLoading = false;
    },
    fetchContactError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.currentContactLoading = false;
      state.currentContact = null;
    },
    createContactStart: (state) => {
      state.loading = true;
    },
    createContactSuccess: (state) => {
      state.newContactModalVisible = false;
      state.loading = false;
      state.contactsListDirty = true;
    },
    createContactError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.loading = false;
    },
    updateContactStart: (state) => {
      state.loading = true;
    },
    updateContactSuccess: (state) => {
      state.loading = false;
      state.contactsListDirty = true;
    },
    updateContactError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.loading = false;
    },
    deleteContactStart: (state) => {
      state.loading = true;
    },
    deleteContactSuccess: (state) => {
      state.loading = false;
      state.contactsListDirty = true;
    },
    deleteContactError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.loading = false;
    },
    showNewContactsModal: (state) => {
      state.newContactModalVisible = true;
    },
    hideNewContactsModal: (state) => {
      state.newContactModalVisible = false;
    },
    showEditContactsModal: (state, action: PayloadAction<any>) => {
      state.currentContactId = action.payload;
      state.editContactModalVisible = true;
    },
    hideEditContactsModal: (state) => {
      state.currentContactId = null;
      state.editContactModalVisible = false;
    },
    clearContactsListDirty: (state) => {
      state.contactsListDirty = false;
    },
    appendVendorList: (state, action: PayloadAction<ContactInfo[]>) => {
      state.contacts.push(...action.payload);
    },
    resetContactsList: () => initialState,
    resetCurrentContact: (state) => {
      state.currentContact = null;
    }
  }
});

export const {
  fetchContactListStart,
  fetchContactListSuccess,
  fetchContactListError,
  clearContactsError,
  fetchContactStart,
  fetchContactSuccess,
  fetchContactError,
  createContactStart,
  createContactSuccess,
  createContactError,
  updateContactStart,
  updateContactSuccess,
  updateContactError,
  deleteContactStart,
  deleteContactSuccess,
  deleteContactError,
  showNewContactsModal,
  hideNewContactsModal,
  showEditContactsModal,
  hideEditContactsModal,
  clearContactsListDirty,
  appendVendorList,
  resetContactsList,
  resetCurrentContact
} = contactsSlice.actions;

export default contactsSlice.reducer;

export function contactOwnedByUser(id: number, idList: number[]): boolean {
  if (idList) {
    return idList.includes(id);
  }
  return false;
}
